<template>
  <div class="add-post-view">
    <!-- Header -->
    <div class="d-flex flex-wrap align-center justify-space-between ga-3 mb-6">
      <div class="d-flex align-center ga-3">
        <v-btn
          to="/books/homeview"
          icon
          variant="tonal"
          size="small"
          aria-label="Back"
        >
          <v-icon size="20">mdi-arrow-left</v-icon>
        </v-btn>
        <div>
          <h1 class="text-h4 font-weight-bold">Add Book</h1>
          <p class="text-caption text-medium-emphasis mb-0">Add a new book or novel to your personal library</p>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="d-flex align-center flex-wrap ga-2">
        <v-btn
          variant="tonal"
          size="default"
          class="btn-modern"
          :disabled="saving || savingWishList"
          :loading="savingWishList"
          prepend-icon="mdi-bookmark-plus-outline"
          @click="saveWishList"
        >
          Save to Wishlist
        </v-btn>

        <v-btn
          color="success"
          size="default"
          class="btn-modern btn-glow-success text-white font-weight-bold"
          :disabled="saving || savingWishList"
          :loading="saving"
          prepend-icon="mdi-check"
          @click="saveBook"
        >
          Save to Collection
        </v-btn>
      </div>
    </div>

    <!-- Alert Messages -->
    <v-alert v-if="error" type="error" class="mb-4" closable variant="tonal" @click:close="error = ''">
      {{ error }}
    </v-alert>

    <v-alert v-if="autofillMessage" type="info" class="mb-4" closable variant="tonal" @click:close="autofillMessage = ''">
      <div class="d-flex align-center">
        <v-icon class="mr-2">mdi-information-outline</v-icon>
        {{ autofillMessage }}
      </div>
    </v-alert>

    <!-- Google Books Smart Autofill Search Bar -->
    <v-card class="glass-card pa-4 mb-6 autofill-card">
      <div class="d-flex align-center ga-2 mb-2">
        <v-icon color="#10b981" size="20">mdi-auto-fix</v-icon>
        <span class="text-subtitle-2 font-weight-bold">Google Books Auto-Fill</span>
      </div>
      <v-row dense align="center">
        <v-col cols="12" sm="5">
          <v-text-field
            v-model="book.title"
            placeholder="Book title (e.g. Dune, 1984, The Hobbit)..."
            density="compact"
            hide-details
            prepend-inner-icon="mdi-magnify"
            variant="outlined"
            @keyup.enter="autofill"
          />
        </v-col>
        <v-col cols="12" sm="4">
          <v-text-field
            v-model="book.author"
            placeholder="Author (optional, e.g. Frank Herbert)..."
            density="compact"
            hide-details
            prepend-inner-icon="mdi-account-edit"
            variant="outlined"
            @keyup.enter="autofill"
          />
        </v-col>
        <v-col cols="12" sm="3" class="d-flex ga-2">
          <v-btn
            color="success"
            variant="flat"
            block
            class="btn-modern text-white font-weight-bold"
            :loading="searchingBooks"
            :disabled="!book.title"
            prepend-icon="mdi-sparkles"
            @click="autofill"
          >
            Auto-Fill
          </v-btn>
        </v-col>
      </v-row>

      <!-- Matches Switcher -->
      <div v-if="searchResults.length > 1" class="mt-3 pt-3 border-t-subtle d-flex align-center justify-space-between flex-wrap ga-2">
        <span class="text-caption text-medium-emphasis">
          Match {{ bookInstance + 1 }} of {{ searchResults.length }}: <strong>{{ searchResults[bookInstance]?.volumeInfo?.title }}</strong>
        </span>
        <v-btn
          size="x-small"
          variant="tonal"
          class="btn-modern"
          prepend-icon="mdi-skip-next"
          @click="autofillNext"
        >
          Try Next Match
        </v-btn>
      </div>
    </v-card>

    <!-- Main Form Grid -->
    <v-form ref="formRef">
      <v-row>
        <!-- Left Column: Cover Preview -->
        <v-col cols="12" md="4" lg="3">
          <v-card class="glass-card pa-4 text-center sticky-poster-card">
            <div class="text-subtitle-2 font-weight-bold mb-3 text-left">Book Cover</div>

            <div class="poster-dropzone mb-3">
              <v-img
                v-if="posterPreview"
                :src="posterPreview"
                :aspect-ratio="2 / 3"
                cover
                class="rounded-lg shadow-md"
              />
              <div v-else class="poster-empty-placeholder">
                <v-icon size="48" color="#10b981" class="mb-2">mdi-book-plus-outline</v-icon>
                <div class="text-caption font-weight-bold">No Cover Selected</div>
                <div class="text-caption text-medium-emphasis">Upload an image or use auto-fill</div>
              </div>
            </div>

            <v-file-input
              v-model="imageFile"
              label="Upload Custom Cover"
              accept="image/*"
              density="compact"
              hide-details
              prepend-icon=""
              prepend-inner-icon="mdi-upload"
              variant="outlined"
              class="mb-2"
            />

            <v-text-field
              v-model="book.imageurl"
              label="Or Cover Image URL"
              placeholder="https://..."
              density="compact"
              hide-details
              variant="outlined"
            />
          </v-card>
        </v-col>

        <!-- Right Column: Form Sections -->
        <v-col cols="12" md="8" lg="9">
          <!-- Section 1: Core Details -->
          <v-card class="glass-card pa-6 mb-6">
            <div class="d-flex align-center ga-2 mb-4">
              <v-icon color="#10b981" size="22">mdi-information-outline</v-icon>
              <h3 class="text-subtitle-1 font-weight-bold mb-0">Book Details</h3>
            </div>

            <v-row dense>
              <v-col cols="12" sm="8">
                <v-text-field
                  v-model="book.title"
                  label="Book Title *"
                  density="comfortable"
                  :rules="[required]"
                />
              </v-col>
              <v-col cols="12" sm="4">
                <v-select
                  v-model="book.category"
                  :items="CATEGORIES"
                  label="Category *"
                  density="comfortable"
                  :rules="[required]"
                />
              </v-col>

              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="book.author"
                  label="Author / Writer"
                  density="comfortable"
                />
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="book.original"
                  label="Original Title (Optional)"
                  density="comfortable"
                />
              </v-col>

              <v-col cols="6" sm="3">
                <v-text-field
                  v-model="book.year"
                  label="Publish Year"
                  placeholder="e.g. 1965"
                  density="comfortable"
                />
              </v-col>
              <v-col cols="6" sm="3">
                <v-text-field
                  v-model="book.publishedDate"
                  label="Published Date"
                  placeholder="YYYY-MM-DD"
                  density="comfortable"
                />
              </v-col>
              <v-col cols="6" sm="3">
                <v-text-field
                  v-model="book.pageCount"
                  label="Page Count"
                  placeholder="e.g. 412"
                  density="comfortable"
                />
              </v-col>
              <v-col cols="6" sm="3">
                <v-select
                  v-model="book.language"
                  :items="LANGUAGES"
                  label="Language"
                  density="comfortable"
                />
              </v-col>
              <v-col cols="6" sm="3">
                <v-text-field
                  v-model="book.type"
                  label="Format / Print Type"
                  placeholder="e.g. BOOK, HARDCOVER"
                  density="comfortable"
                />
              </v-col>

              <!-- Star Rating -->
              <v-col cols="12" sm="9" class="d-flex align-center ga-3 mt-2">
                <span class="text-caption font-weight-bold text-medium-emphasis">Your Rating:</span>
                <v-rating
                  v-model="ratingNumber"
                  half-increments
                  density="comfortable"
                  color="amber"
                  active-color="amber"
                />
                <span class="text-body-2 font-weight-bold">{{ ratingNumber }} / 5</span>
              </v-col>
            </v-row>
          </v-card>

          <!-- Section 2: Description -->
          <v-card class="glass-card pa-6 mb-6">
            <div class="d-flex align-center ga-2 mb-4">
              <v-icon color="#10b981" size="22">mdi-book-open-page-variant-outline</v-icon>
              <h3 class="text-subtitle-1 font-weight-bold mb-0">Summary & Description</h3>
            </div>

            <v-textarea
              v-model="book.description"
              label="Book Description / Plot Summary"
              rows="5"
              auto-grow
              density="comfortable"
              variant="outlined"
            />
          </v-card>
        </v-col>
      </v-row>
    </v-form>
  </div>
</template>

<script>
import axios from 'axios'
import { computed, reactive, ref } from 'vue'
import { useRouter } from 'vue-router'
import { BOOKS_API_KEY, booksApiUrl } from '@/config/googlebooks'
import { CATEGORIES, LANGUAGES, QUALITIES } from '@/config/books'
import BooksAPI from '@/services/books'
import { appendBookFormData, defaultBookForm } from '@/utils/bookForm'
import { apiErrorMessage } from '@/utils/formValidate'

export default {
  name: 'AddBookPost',
  setup() {
    const router = useRouter()
    const formRef = ref(null)
    const book = reactive(defaultBookForm())
    const imageFile = ref(null)
    const saving = ref(false)
    const savingWishList = ref(false)
    const searchingBooks = ref(false)
    const error = ref('')
    const autofillMessage = ref('')
    const bookInstance = ref(0)
    const searchResults = ref([])

    const required = (value) => !!value || 'This field is required'

    const ratingNumber = computed({
      get: () => Number(book.rating) || 0,
      set: (value) => {
        book.rating = String(value)
      },
    })

    const posterPreview = computed(() => {
      if (book.imageurl) return book.imageurl
      const file = imageFile.value
      if (!file) return ''
      const selected = Array.isArray(file) ? file[0] : file
      return selected ? URL.createObjectURL(selected) : ''
    })

    const buildQuery = () => {
      const parts = []
      const title = String(book.title || '').trim()
      const author = String(book.author || '').trim()
      if (title) parts.push(`intitle:${title}`)
      if (author) parts.push(`inauthor:${author}`)
      return parts.join(' ')
    }

    const autofill = async () => {
      autofillMessage.value = ''
      error.value = ''
      if (!book.title || !book.title.trim()) return

      if (!BOOKS_API_KEY) {
        autofillMessage.value = 'Google Books API key not configured. You can manually enter details.'
        return
      }

      searchingBooks.value = true
      try {
        if (!searchResults.value.length) {
          const { data } = await axios.get(booksApiUrl, {
            params: { key: BOOKS_API_KEY, q: buildQuery(), langRestrict: 'fr' },
          })
          searchResults.value = data.items || []
          bookInstance.value = 0
        }
        if (!searchResults.value.length) {
          autofillMessage.value = 'No Google Books results found for this title.'
          return
        }
        applyGoogleBooksResult(searchResults.value[bookInstance.value])
      } catch (err) {
        error.value = err.message || 'Google Books autofill failed'
      } finally {
        searchingBooks.value = false
      }
    }

    const autofillNext = () => {
      if (!searchResults.value.length) {
        autofill()
        return
      }
      bookInstance.value = (bookInstance.value + 1) % searchResults.value.length
      applyGoogleBooksResult(searchResults.value[bookInstance.value])
    }

    const applyGoogleBooksResult = (result) => {
      if (!result) return
      const info = result.volumeInfo || {}
      if (info.title) book.title = info.title
      const publishedDate = (info.publishedDate || '').substring(0, 10)
      book.year = publishedDate.substring(0, 4)
      book.publishedDate = publishedDate
      book.pageCount = info.pageCount != null ? String(info.pageCount) : ''
      book.type = info.printType || ''
      const thumbnail = info.imageLinks?.thumbnail || info.imageLinks?.smallThumbnail || ''
      book.imageurl = thumbnail.replace(/^http:/, 'https:')
      book.description = info.description || ''
      book.author = (info.authors || []).join(', ')

      autofillMessage.value = `Loaded: "${info.title || 'result'}" (${book.year || 'N/A'})`
    }

    const savePost = async (markedValue = '') => {
      error.value = ''
      const title = String(book.title || '').trim()
      const category = String(book.category || '').trim()

      if (!title || !category) {
        error.value = 'Title and category are required before saving.'
        return
      }

      book.title = title
      book.category = category
      book.marked = markedValue

      const isWishList = markedValue === '2'
      if (isWishList) {
        savingWishList.value = true
      } else {
        saving.value = true
      }

      try {
        const formData = new FormData()
        appendBookFormData(formData, book)
        if (imageFile.value) {
          const file = Array.isArray(imageFile.value) ? imageFile.value[0] : imageFile.value
          if (file) formData.append('image', file)
        }
        await BooksAPI.addPost(formData)
        const message = isWishList ? 'Book added to wish list.' : 'Book added to collection.'
        router.push({ name: 'BooksHome', query: { message } })
      } catch (err) {
        error.value = apiErrorMessage(err, 'Failed to save book')
      } finally {
        saving.value = false
        savingWishList.value = false
      }
    }

    const saveBook = () => savePost('0')
    const saveWishList = () => savePost('2')

    return {
      formRef,
      book,
      imageFile,
      saving,
      savingWishList,
      searchingBooks,
      error,
      autofillMessage,
      searchResults,
      bookInstance,
      CATEGORIES,
      LANGUAGES,
      QUALITIES,
      required,
      ratingNumber,
      posterPreview,
      autofill,
      autofillNext,
      saveBook,
      saveWishList,
    }
  },
}
</script>

<style scoped>
.add-post-view {
  max-width: 1240px;
  margin: 0 auto;
}

.autofill-card {
  border: 1px solid rgba(16, 185, 129, 0.3) !important;
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.06), rgba(52, 211, 153, 0.04)) !important;
}

.sticky-poster-card {
  position: sticky;
  top: 80px;
}

.poster-dropzone {
  position: relative;
  aspect-ratio: 2 / 3;
  border-radius: 12px;
  overflow: hidden;
}

.poster-empty-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  border: 2px dashed rgba(128, 128, 128, 0.3);
  border-radius: 12px;
  padding: 16px;
  background: rgba(128, 128, 128, 0.05);
}

.border-t-subtle {
  border-top: 1px solid rgba(128, 128, 128, 0.12);
}
</style>

